<template lang="pug">
.d-flex.flex-wrap.align-center.gap-2.my-4
  .d-flex.flex-wrap.align-center.gap-2.flex-grow-1
    filter-name(ref="nameFilter" :filter.sync="nameFilter" :check-initial-value="true")
    filter-category(ref="categoryFilter" :filter.sync="categoryFilter" :check-initial-value="true")
    filter-group(ref="groupFilter" :filter.sync="groupFilter" :check-initial-value="true")
    filter-cost-price(ref="costPriceFilter" :filter.sync="costPriceFilter" :check-initial-value="true")
    filter-price-to-patient(ref="priceToPatientFilter" :filter.sync="priceToPatientFilter" :check-initial-value="true")
    filter-stock(ref="stockFilter" :filter.sync="stockFilter" :check-initial-value="true")
    filter-expiry-date(ref="expiryDateFilter" :filter.sync="expiryDateFilter" :check-initial-value="true")
  yz-btn(label="Reset filters" text underline @click="onResetFilters" :disabled="!hasActiveFilters")
</template>

<script>
import FilterName from './filters/FilterName.vue'
import FilterCategory from './filters/FilterCategory.vue'
import FilterGroup from './filters/FilterGroup.vue'
import FilterCostPrice from './filters/FilterCostPrice.vue'
import FilterPriceToPatient from './filters/FilterPriceToPatient.vue'
import FilterStock from './filters/FilterStock.vue'
import FilterExpiryDate from './filters/FilterExpiryDate.vue'

export default {
  components: {
    FilterName,
    FilterCategory,
    FilterGroup,
    FilterCostPrice,
    FilterPriceToPatient,
    FilterStock,
    FilterExpiryDate
  },
  props: {
    filters: { type: Array, default: () => [] }
  },
  data: () => ({
    isInitializing: false,
    nameFilter: {},
    categoryFilter: {},
    groupFilter: {},
    costPriceFilter: {},
    priceToPatientFilter: {},
    stockFilter: {},
    expiryDateFilter: {}
  }),
  computed: {
    allFilters () {
      const filters = []
      if (this.nameFilter.key) filters.push(this.nameFilter)
      if (this.categoryFilter.key) filters.push(this.categoryFilter)
      if (this.groupFilter.key) filters.push(this.groupFilter)
      if (this.costPriceFilter.key) {
        if (this.costPriceFilter.key === 'between') {
          const values = this.costPriceFilter.value
          for (const i in values) { filters.push(values[i]) }
        } else {
          filters.push(this.costPriceFilter)
        }
      }
      if (this.priceToPatientFilter.key) {
        if (this.priceToPatientFilter.key === 'between') {
          const values = this.priceToPatientFilter.value
          for (const i in values) { filters.push(values[i]) }
        } else {
          filters.push(this.priceToPatientFilter)
        }
      }
      if (this.stockFilter.key) {
        if (this.stockFilter.key === 'between') {
          const values = this.stockFilter.value
          for (const i in values) { filters.push(values[i]) }
        } else {
          filters.push(this.stockFilter)
        }
      }
      if (this.expiryDateFilter.key) filters.push(this.expiryDateFilter)
      return filters
    },
    hasActiveFilters () {
      return this.nameFilter.key || this.categoryFilter.key || this.groupFilter.key ||
        this.costPriceFilter.key || this.priceToPatientFilter.key ||
        this.stockFilter.key || this.expiryDateFilter.key
    }
  },
  watch: {
    filters: {
      immediate: true,
      handler (newFilters) {
        this.initializeFromFilters(newFilters)
      }
    },
    allFilters (newValue) {
      if (!this.isInitializing) {
        this.$emit('update:filters', newValue)
      }
    }
  },
  methods: {
    async initializeFromFilters (filters) {
      if (!filters || filters.length === 0) return
      if (this.isInitializing) return
      if (JSON.stringify(filters) === JSON.stringify(this.allFilters)) return

      this.isInitializing = true

      try {
        // Name filter
        const nameKeys = ['name', 'name__ne', 'name__contains', 'name__exclude', 'name__startswith', 'name__endswidth']
        const nameFilterData = filters.find(f => nameKeys.includes(f.key))
        if (nameFilterData) {
          this.nameFilter = { ...nameFilterData }
          await this.$nextTick()
          if (this.$refs.nameFilter) {
            this.$refs.nameFilter.option = nameFilterData.key
            this.$refs.nameFilter.value = nameFilterData.value
            this.$refs.nameFilter.selectedOption = nameFilterData.key
            this.$refs.nameFilter.selectedValue = nameFilterData.value
          }
        }

        // Category filter
        const categoryFilterData = filters.find(f => f.key === 'category')
        if (categoryFilterData) {
          this.categoryFilter = { ...categoryFilterData }
          await this.$nextTick()
          if (this.$refs.categoryFilter) {
            this.$refs.categoryFilter.selectedCategory = categoryFilterData.value
            this.$refs.categoryFilter.tempSelectedCategory = categoryFilterData.value
            this.$refs.categoryFilter.onSelectCategory(categoryFilterData.value, true)
          }
        }

        // Group filter
        const groupFilterData = filters.find(f => f.key === 'groups')
        if (groupFilterData) {
          this.groupFilter = { ...groupFilterData }
        }

        // Cost price filter
        const costKeys = ['cost', 'cost__ne', 'cost__lt', 'cost__gt', 'cost__gte', 'cost__lte', 'between']
        const costFilterData = filters.find(f => costKeys.includes(f.key))
        if (costFilterData) {
          this.costPriceFilter = { ...costFilterData }
          await this.$nextTick()
          if (this.$refs.costPriceFilter) {
            this.$refs.costPriceFilter.selectedOption = costFilterData.key
            this.$refs.costPriceFilter.option = costFilterData.key
            if (costFilterData.key === 'between' && Array.isArray(costFilterData.value)) {
              this.$refs.costPriceFilter.value = Number(costFilterData.value[0].value) / Number(this.$currency.multiplier)
              this.$refs.costPriceFilter.endValue = Number(costFilterData.value[1].value) / Number(this.$currency.multiplier)
              this.$refs.costPriceFilter.selectedValue = this.$refs.costPriceFilter.value
              this.$refs.costPriceFilter.selectedEndValue = this.$refs.costPriceFilter.endValue
            } else {
              this.$refs.costPriceFilter.value = Number(costFilterData.value) / Number(this.$currency.multiplier)
              this.$refs.costPriceFilter.selectedValue = this.$refs.costPriceFilter.value
            }
          }
        }

        // Price to patient filter
        const priceKeys = ['price', 'price__ne', 'price__lt', 'price__gt', 'price__gte', 'price__lte']
        const priceFilterData = filters.find(f => priceKeys.includes(f.key))
        if (!priceFilterData) {
          // Check for between with price keys inside value array
          const priceBetween = filters.find(f => f.key === 'between' && Array.isArray(f.value) && f.value.some(v => v.key && v.key.startsWith('price')))
          if (priceBetween) {
            this.priceToPatientFilter = { ...priceBetween }
            await this.$nextTick()
            if (this.$refs.priceToPatientFilter) {
              this.$refs.priceToPatientFilter.selectedOption = 'between'
              this.$refs.priceToPatientFilter.option = 'between'
              this.$refs.priceToPatientFilter.value = Number(priceBetween.value[0].value) / Number(this.$currency.multiplier)
              this.$refs.priceToPatientFilter.endValue = Number(priceBetween.value[1].value) / Number(this.$currency.multiplier)
              this.$refs.priceToPatientFilter.selectedValue = this.$refs.priceToPatientFilter.value
              this.$refs.priceToPatientFilter.selectedEndValue = this.$refs.priceToPatientFilter.endValue
            }
          }
        } else {
          this.priceToPatientFilter = { ...priceFilterData }
          await this.$nextTick()
          if (this.$refs.priceToPatientFilter) {
            this.$refs.priceToPatientFilter.selectedOption = priceFilterData.key
            this.$refs.priceToPatientFilter.option = priceFilterData.key
            this.$refs.priceToPatientFilter.value = Number(priceFilterData.value) / Number(this.$currency.multiplier)
            this.$refs.priceToPatientFilter.selectedValue = this.$refs.priceToPatientFilter.value
          }
        }

        // Stock filter
        const stockKeys = ['stock', 'stock__ne', 'stock__lt', 'stock__gt', 'stock__gte', 'stock__lte']
        const stockFilterData = filters.find(f => stockKeys.includes(f.key))
        if (!stockFilterData) {
          const stockBetween = filters.find(f => f.key === 'between' && Array.isArray(f.value) && f.value.some(v => v.key && v.key.startsWith('stock')))
          if (stockBetween) {
            this.stockFilter = { ...stockBetween }
            await this.$nextTick()
            if (this.$refs.stockFilter) {
              this.$refs.stockFilter.selectedOption = 'between'
              this.$refs.stockFilter.option = 'between'
              this.$refs.stockFilter.value = stockBetween.value[0].value
              this.$refs.stockFilter.endValue = stockBetween.value[1].value
              this.$refs.stockFilter.selectedValue = this.$refs.stockFilter.value
              this.$refs.stockFilter.selectedEndValue = this.$refs.stockFilter.endValue
            }
          }
        } else {
          this.stockFilter = { ...stockFilterData }
          await this.$nextTick()
          if (this.$refs.stockFilter) {
            this.$refs.stockFilter.selectedOption = stockFilterData.key
            this.$refs.stockFilter.option = stockFilterData.key
            this.$refs.stockFilter.value = stockFilterData.value
            this.$refs.stockFilter.selectedValue = stockFilterData.value
          }
        }

        // Expiry date filter
        const expiryFilterData = filters.find(f => f.key === 'expiry_date')
        if (expiryFilterData) {
          this.expiryDateFilter = { ...expiryFilterData }
          await this.$nextTick()
          if (this.$refs.expiryDateFilter) {
            this.$refs.expiryDateFilter.dateFilter = {
              active: true,
              value: Array.isArray(expiryFilterData.value) ? expiryFilterData.value.join(' - ') : expiryFilterData.value,
              tempValue: expiryFilterData.value
            }
          }
        }
      } finally {
        this.isInitializing = false
      }
    },
    resetFilters () {
      this.nameFilter = {}
      this.categoryFilter = {}
      this.groupFilter = {}
      this.costPriceFilter = {}
      this.priceToPatientFilter = {}
      this.stockFilter = {}
      this.expiryDateFilter = {}
    },
    onResetFilters () {
      this.resetFilters()
      this.$emit('reset')
    }
  }
}
</script>
